import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useLatestRef } from "../ref/useLatestRef";

interface AppHistory extends Omit<ReturnType<typeof useHistory>, "goBack"> {
  goBack: (countOfTryingGoBack?: number) => void;
}

export function useAppHistory(): AppHistory {
  const history = useHistory();
  const location = useLocation();

  const locationRef = useLatestRef(location); // memoize as changes on navigation
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Using setInterval to handle an edge case:
  // countOfTryingGoBack defines a limit for goBack() attempts to prevent infinite loops.
  // Example edge case: if the "prev" page redirects twice in a row to the same URL,
  // a single goBack() call would leave the user on the same page they are trying to exit.
  // This interval-based trick repeatedly calls goBack() until the URL changes
  // or the maximum number of attempts (countOfTryingGoBack) is reached.

  // The 100 ms delay is a trade-off between responsiveness and performance:
  // - Short enough to quickly detect URL changes after goBack().
  // - Long enough to avoid excessive calls, especially in cases with slower transitions
  //   (e.g., network delays or heavy applications).

  const goBack = useCallback(
    (countOfTryingGoBack: number = 3) => {
      const initialUrl = locationRef.current.pathname;
      let attempts = 0;

      const interval = setInterval(() => {
        if (locationRef.current.pathname !== initialUrl || attempts >= countOfTryingGoBack) {
          clearInterval(interval);
          intervalIdRef.current = null;
        } else {
          history.goBack();
        }
        attempts += 1;
      }, 100);

      intervalIdRef.current = interval;
    },
    [history, locationRef],
  );

  // cleanup
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  // must return same instance if original history not changed
  const historyComputed = useMemo(() => ({ ...history, goBack }), [history, goBack]);
  return historyComputed;
}
